import { useCallback, useState, useEffect, useRef } from 'react';
import { Flex } from 'components/box';
import {
  ProductGalleryItemInterface,
  SoldOutHeaderInterface,
} from 'types/types';
import { isGalleryVideo } from 'types/guards/product';
import SoldOutMask from 'components/sold-out-mask';
import ThumbnailBullets from 'components/_shared/widgets/thumbnail-bullets';
import resourceUrl from '../../../../../util/make-url';
import { css } from '@emotion/react';
import dynamic from 'next/dynamic';
import ProductLabel from './product-label';

const EmbeddedVideo = dynamic(
  () => import('components/_shared/widgets/embedded-video')
);

const HIDE_UI_TIMEOUT = 3500;
const FADE_IN_TIME = 0.2;

const Swiper = ({
  media,
  activeMediaIndex,
  setActiveMediaIndex,
  soldOut,
  handleBulletVisibility,
  dealTypes,
  soldOutHeaders,
}: {
  media: ProductGalleryItemInterface[];
  activeMediaIndex: number;
  setActiveMediaIndex: (num: number) => void;
  soldOut: boolean;
  handleBulletVisibility: () => void;
  dealTypes?: string[];
  soldOutHeaders?: SoldOutHeaderInterface[];
}) => {
  const portal = useRef<HTMLDivElement>(null);

  const [slideWidth, setSlideWidth] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [soldOutMaskVisible, setSoldOutMaskVisible] = useState(soldOut);

  const hideSoldOutMask = useCallback(() => setSoldOutMaskVisible(false), []);

  const showSoldOutMask = useCallback(() => {
    soldOut && setSoldOutMaskVisible(true);
  }, [soldOut]);

  useEffect(() => {
    const setSliderWindowSize = () => {
      if (portal.current) {
        setSlideWidth(portal.current.offsetWidth);
      }
    };
    setSliderWindowSize();
    window.addEventListener('resize', setSliderWindowSize, { passive: true });
    return () => {
      window.removeEventListener('resize', setSliderWindowSize);
    };
  }, []);

  useEffect(() => {
    const portalRef = portal.current;
    if (!portalRef) return;

    let scrollTimer: NodeJS.Timeout | undefined;

    const handleSliderScroll = () => {
      if (slideWidth <= 0) return;
      setIsScrolling(true);
      const newIndex = Math.round(portalRef.scrollLeft / slideWidth);
      if (newIndex !== activeMediaIndex) {
        setActiveMediaIndex(newIndex);
      }
      handleBulletVisibility();
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        setIsScrolling(false);
      }, 200); // Change this value as needed to adjust the sensitivity of the scrollend event
    };

    portalRef.addEventListener('scroll', handleSliderScroll, { passive: true });
    return () => {
      portalRef.removeEventListener('scroll', handleSliderScroll);
      clearTimeout(scrollTimer);
    };
  }, [
    activeMediaIndex,
    handleBulletVisibility,
    setActiveMediaIndex,
    slideWidth,
  ]);

  /**
   * Jump to specific variant.
   */
  useEffect(() => {
    const portalRef = portal.current;
    if (!portalRef || slideWidth === 0) return;
    if (!isScrolling) {
      const targetScroll = activeMediaIndex * slideWidth;
      if (Math.abs(portalRef.scrollLeft - targetScroll) > 10) {
        portalRef.scrollTo({ left: targetScroll, behavior: 'smooth' });
      }
    }
  }, [activeMediaIndex, slideWidth, isScrolling]);

  useEffect(() => {
    showSoldOutMask();
  }, [activeMediaIndex, showSoldOutMask]);

  return (
    <Flex
      ref={portal}
      css={theme => css`
        box-shadow: ${theme.shadows.products};
        position: relative;
        overflow-x: scroll;
        overflow-y: hidden;
        scroll-snap-type: x mandatory;
        scrollbar-width: 0;
        scrollbar-color: transparent;
        -ms-overflow-style: none;
        -webkit-overflow-scrolling: touch;
        margin-left: -16px;
        margin-right: -16px;
        margin-top: -16px;
        ::-webkit-scrollbar {
          display: none;
        }
        ::-webkit-scrollbar-thumb {
          background: transparent;
        }
      `}
    >
      {media.map((media, idx) => (
        <Flex
          key={media.file.url}
          css={css`
            position: relative;
            width: 100%;
            height: 100%;
            flex-shrink: 0;
            scroll-snap-stop: always;
            scroll-snap-align: center;
            align-self: left;
          `}
          p={0}
          onClick={hideSoldOutMask}
          onKeyDown={hideSoldOutMask}
          tabIndex={0}
          justifyContent="center"
          alignItems="center"
        >
          {soldOutMaskVisible && (
            <SoldOutMask
              clock
              text
              soldOutHeaders={soldOutHeaders}
              dealTypes={dealTypes}
            />
          )}
          {isGalleryVideo(media) ? (
            <EmbeddedVideo
              title={media.label}
              url={media.video.url}
              provider={media.video.provider}
              isVideoActive={activeMediaIndex === idx}
            />
          ) : (
            <img
              alt={media.label || 'Product media'}
              css={css`
                width: 100%;
                height: auto;
                max-width: 100%;
                max-height: 100%;
                object-fit: contain;
                backface-visibility: hidden;
                animation-duration: 1s;
                animation-fill-mode: both;
                animation-name: fadeIn;
              `}
              src={resourceUrl(media.file.url, {
                width: 800,
                height: 800,
                bg: 'fff',
                fit: 'fill',
              })}
            />
          )}

          {media.label && <ProductLabel text={media.label} />}
        </Flex>
      ))}
    </Flex>
  );
};

const MobileCarousel = ({
  media,
  activeMediaIndex,
  setActiveMediaIndex,
  isSoldOut,
  hideThumbnail = false,
  mobileBulletHeight,
  soldOutHeaders,
  dealTypes,
}: {
  media: ProductGalleryItemInterface[];
  isSoldOut: boolean;
  activeMediaIndex: number;
  setActiveMediaIndex: (idx: number) => void;
  hideThumbnail?: boolean;
  isPromotionalDeal?: boolean;
  price?: string;
  mobileBulletHeight: string;
  soldOutHeaders?: SoldOutHeaderInterface[];
  dealTypes?: string[];
}) => {
  const [showBullets, setShowBullets] = useState(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleBulletVisibility = useCallback(() => {
    setShowBullets(true);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setShowBullets(false);
    }, HIDE_UI_TIMEOUT);
  }, []);

  /**
   * Fade out after initial render.
   */
  useEffect(() => {
    handleBulletVisibility();
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [handleBulletVisibility]);

  return (
    <>
      <Swiper
        media={media}
        setActiveMediaIndex={setActiveMediaIndex}
        activeMediaIndex={activeMediaIndex}
        soldOut={isSoldOut}
        handleBulletVisibility={handleBulletVisibility}
        soldOutHeaders={soldOutHeaders}
        dealTypes={dealTypes}
      />
      {showBullets && !hideThumbnail && (
        <ThumbnailBullets
          mobileBulletHeight={mobileBulletHeight}
          total={media.length}
          activeIndex={activeMediaIndex}
          css={css`
            animation: fadeIn ${FADE_IN_TIME}s;
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
          `}
        />
      )}
    </>
  );
};

export default MobileCarousel;
